import { useEffect, useRef, useState } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi, ChartOptions, DeepPartial, Time, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import { Candle } from '../../interface/Candle';
import { RSIItem } from '../../interface/RSIItem';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import { LogApi } from '../../api/LogApi';
import Box from '@mui/material/Box';
import { useLogs } from '../../hooks/useLogs';

interface MarketChartProps {
  chartInterval: string;
  tradingPair: string;
  height?: number;
  rsiThresholdBuy?: number;
  rsiThresholdSell?: number;
  entryPrice?: number | string;
  currentPrice?: number | string;
  takeProfit?: number | string;
  stopLoss?: number | string;
  executionTimestamp?: number;
  executionPrice?: number | string;
  executionType?: 'BUY' | 'SELL';
  candleFromTime?: string;
}

interface CandlestickData {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface LineData {
  time: Time;
  value: number;
}

export const MarketChart = ({
  chartInterval,
  tradingPair,
  height = 450,
  rsiThresholdBuy = 30,
  rsiThresholdSell = 70,
  entryPrice,
  currentPrice,
  takeProfit,
  stopLoss,
  executionTimestamp,
  executionPrice,
  executionType,
  candleFromTime,
}: MarketChartProps) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const rsiChartContainerRef = useRef<HTMLDivElement>(null);
  const [candles, setCandles] = useState<Candle[] | undefined>(undefined);
  const [rsiData, setRsiData] = useState<RSIItem[] | undefined>(undefined);
  const chartRef = useRef<IChartApi | null>(null);
  const rsiChartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const rsiSeriesRef = useRef<any>(null);

  const { getLogs, logs } = useLogs();

  // Fetch candle data, RSI data, and logs if needed
  useEffect(() => {
    getCandles();
    getRsiData();
  }, [chartInterval, tradingPair, candleFromTime]);

  const getCandles = async () => {
    if (tradingPair && chartInterval) {
      try {
        const logApi = new LogApi();
        const candles = await logApi.getCandles(tradingPair, chartInterval, candleFromTime);
        if (candles && candles.length > 0) {
          await getLogs(moment(candles[0].closeTime).toISOString(), tradingPair, 200);
        }
        setCandles(candles);

      } catch (error) {
        console.error("Error getting Candles", error);
      }
    }
  };

  const getRsiData = async () => {
    if (tradingPair && chartInterval) {
      try {
        const logApi = new LogApi();
        const rsiData = await logApi.getRSI(tradingPair, chartInterval);
        setRsiData(rsiData);
      } catch (error) {
        console.error("Error getting RSI data", error);
      }
    }
  };

  // Convert candle data to the format expected by lightweight-charts
  const formatCandleData = (candles: Candle[]): CandlestickData[] => {
    return candles.map(candle => ({
      time: candle.openTime / 1000 as Time, // Convert to seconds for lightweight-charts
      open: parseFloat(candle.open),
      high: parseFloat(candle.high),
      low: parseFloat(candle.low),
      close: parseFloat(candle.close),
    }));
  };

  // Format RSI data for the chart
  const formatRsiData = (rsiData: RSIItem[]): LineData[] => {
    return rsiData.map(item => ({
      time: new Date(item.timestamp).getTime() / 1000 as Time,
      value: item.rsi,
    }));
  };

  // Initialize charts
  useEffect(() => {
    if (chartContainerRef.current && candles && candles.length > 0) {
      const hasExecutionFocus = Number.isFinite(Number(executionTimestamp));

      // Clean up previous charts if they exist
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        candleSeriesRef.current = null;
      }
      
      if (rsiChartRef.current) {
        rsiChartRef.current.remove();
        rsiChartRef.current = null;
        rsiSeriesRef.current = null;
      }

      // Common chart options
      const commonOptions: DeepPartial<ChartOptions> = {
        layout: {
          background: { type: ColorType.Solid, color: 'transparent' },
          textColor: '#d1d4dc',
        },
        grid: {
          vertLines: { color: 'rgba(42, 46, 57, 0.5)' },
          horzLines: { color: 'rgba(42, 46, 57, 0.5)' },
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: 'rgba(197, 203, 206, 0.8)',
          rightOffset: 5,
          barSpacing: 10,
          fixLeftEdge: !hasExecutionFocus,
          lockVisibleTimeRangeOnResize: true,
          rightBarStaysOnScroll: true,
        },
        crosshair: {
          mode: 1,
          vertLine: {
            width: 1,
            color: 'rgba(224, 227, 235, 0.1)',
            style: 0,
          },
          horzLine: {
            width: 1,
            color: 'rgba(224, 227, 235, 0.1)',
            style: 0,
            labelBackgroundColor: 'rgba(42, 46, 57, 0.8)',
          },
        },
      };

      // Create main price chart
      const mainChartOptions: DeepPartial<ChartOptions> = {
        ...commonOptions,
        width: chartContainerRef.current.clientWidth,
        height: height * 0.7
      };
      
      const mainChart = createChart(chartContainerRef.current, mainChartOptions);
      chartRef.current = mainChart;

      // Add candlestick series
      const candlestickSeries = mainChart.addSeries(CandlestickSeries);
      
      // Apply options
      candlestickSeries.applyOptions({
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
      });
      
      candleSeriesRef.current = candlestickSeries as any;

      // Set data
      const formattedData = formatCandleData(candles);
      candlestickSeries.setData(formattedData);

      const addPriceLine = (priceValue: number, color: string, title: string) => {
        candlestickSeries.createPriceLine({
          price: priceValue,
          color,
          lineWidth: 2,
          lineStyle: 2,
          axisLabelVisible: true,
          title: `${title} $${priceValue.toFixed(8)}`,
        });
      };

      const entryValue = Number(entryPrice);
      if (Number.isFinite(entryValue)) {
        addPriceLine(entryValue, '#42A5F5', 'Entry');
      }

      const currentValue = Number(currentPrice);
      if (Number.isFinite(currentValue)) {
        addPriceLine(currentValue, '#90CAF9', 'Current');
      }

      const takeProfitValue = Number(takeProfit);
      if (Number.isFinite(takeProfitValue)) {
        addPriceLine(takeProfitValue, '#66BB6A', 'Take Profit');
      }

      const stopLossValue = Number(stopLoss);
      if (Number.isFinite(stopLossValue)) {
        addPriceLine(stopLossValue, '#EF5350', 'Stop Loss');
      }

      const executionTimeValue = Number(executionTimestamp);
      const executionPriceValue = Number(executionPrice);
      if (Number.isFinite(executionTimeValue) && Number.isFinite(executionPriceValue)) {
        const executionLabel = executionType === 'SELL' ? 'Sold' : 'Bought';
        createSeriesMarkers(candlestickSeries, [
          {
            time: Math.floor(executionTimeValue / 1000) as Time,
            position: executionType === 'SELL' ? 'aboveBar' : 'belowBar',
            color: executionType === 'SELL' ? '#ef5350' : '#26a69a',
            shape: executionType === 'SELL' ? 'arrowDown' : 'arrowUp',
            text: `${executionLabel} @ ${executionPriceValue.toFixed(6)}`,
          },
        ]);
      }

      // Add markers for buy/sell events
      if (logs && logs.length > 0) {
        logs.forEach(log => {
          if ((log.didBuy || log.didSell) && log.timestamp && log.lastPrice) {
            const eventType = log.didBuy ? 'BUY' : 'SELL';
            candlestickSeries.createPriceLine({
              price: log.lastPrice,
              color: log.didBuy ? '#26a69a' : '#ef5350',
              lineWidth: 2,
              lineStyle: 2,
              axisLabelVisible: true,
              title: `${eventType} at $${log.lastPrice.toFixed(2)}`,
            });
          }
        });
      }

      // Create RSI chart if enabled
      if (rsiChartContainerRef.current && rsiData && rsiData.length > 0) {
        const rsiChartOptions: DeepPartial<ChartOptions> = {
          ...commonOptions,
          width: rsiChartContainerRef.current.clientWidth,
          height: height * 0.3, // 30% of total height
          timeScale: {
            ...commonOptions.timeScale,
            visible: false, // Hide time scale on RSI chart
          },
        };
        
        const rsiChart = createChart(rsiChartContainerRef.current, rsiChartOptions);
        rsiChartRef.current = rsiChart;
        
        // Add RSI line series
        const rsiSeries = rsiChart.addSeries(LineSeries);
        
        // Apply options for RSI line
        rsiSeries.applyOptions({
          color: '#FBC02D',
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
          title: 'RSI',
        });
        
        rsiSeriesRef.current = rsiSeries;
        
        // Format and set RSI data
        const formattedRsiData = formatRsiData(rsiData);
        rsiSeries.setData(formattedRsiData);
        
        // Add horizontal lines for overbought/oversold levels
        const overboughtLine = rsiChart.addSeries(LineSeries);
        overboughtLine.applyOptions({
          color: 'rgba(255, 0, 0, 0.5)',
          lineWidth: 1,
          lineStyle: 2, // Dashed line
          title: 'Overbought',
        });
        overboughtLine.setData([
          { time: formattedRsiData[0].time, value: rsiThresholdSell },
          { time: formattedRsiData[formattedRsiData.length - 1].time, value: rsiThresholdSell }
        ]);
        
        const oversoldLine = rsiChart.addSeries(LineSeries);
        oversoldLine.applyOptions({
          color: 'rgba(0, 255, 0, 0.5)',
          lineWidth: 1,
          lineStyle: 2, // Dashed line
          title: 'Oversold',
        });
        oversoldLine.setData([
          { time: formattedRsiData[0].time, value: rsiThresholdBuy },
          { time: formattedRsiData[formattedRsiData.length - 1].time, value: rsiThresholdBuy }
        ]);
        
        // Sync the two charts' time scales
        mainChart.timeScale().subscribeVisibleTimeRangeChange(() => {
          const mainTimeRange = mainChart.timeScale().getVisibleRange();
          if (mainTimeRange && rsiChart) {
            rsiChart.timeScale().setVisibleRange(mainTimeRange);
          }
        });
        
        // rsiChart.timeScale().subscribeVisibleTimeRangeChange(() => {
        //   const rsiTimeRange = rsiChart.timeScale().getVisibleRange();
        //   if (rsiTimeRange && mainChart) {
        //     mainChart.timeScale().setVisibleRange(rsiTimeRange);
        //   }
        // });
      }

      const executionTimeSeconds = Number.isFinite(executionTimeValue)
        ? Math.floor(executionTimeValue / 1000)
        : undefined;
      const executionIndex = executionTimeSeconds !== undefined
        ? formattedData.findIndex((candle) => Number(candle.time) >= executionTimeSeconds)
        : -1;
      const focusedIndex = executionIndex >= 0
        ? executionIndex
        : Math.max(0, formattedData.length - 1);

      // Open around the execution candle when available so historical orders don't land off-screen.
      const visibleLogicalRange = executionIndex >= 0
        ? {
            from: Math.max(-20, focusedIndex - 72),
            to: Math.min(formattedData.length + 8, focusedIndex + 18),
          }
        : {
            from: formattedData.length > 100 ? formattedData.length - 100 : 0,
            to: formattedData.length + 5,
          };
      
      // Apply the range to both charts
      mainChart.timeScale().setVisibleLogicalRange(visibleLogicalRange);
      if (rsiChartRef.current) {
        rsiChartRef.current.timeScale().setVisibleLogicalRange(visibleLogicalRange);
      }

      // Handle window resize
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({ 
            width: chartContainerRef.current.clientWidth 
          });
        }
        
        if (rsiChartContainerRef.current && rsiChartRef.current) {
          rsiChartRef.current.applyOptions({ 
            width: rsiChartContainerRef.current.clientWidth 
          });
        }
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
          candleSeriesRef.current = null;
        }
        
        if (rsiChartRef.current) {
          rsiChartRef.current.remove();
          rsiChartRef.current = null;
          rsiSeriesRef.current = null;
        }
      };
    }
  }, [
    candles,
    rsiData,
    logs,
    height,
    rsiThresholdBuy,
    rsiThresholdSell,
    entryPrice,
    currentPrice,
    takeProfit,
    stopLoss
    ,
    executionTimestamp,
    executionPrice,
    executionType,
  ]);

  // Format the time for display
  const formatTime = (timestamp: number) => {
    return moment.unix(timestamp / 1000).format("DD-MMM HH:mm");
  };



  return (
    <Box sx={styles.container}>
      <Typography color="textSecondary" sx={styles.title}>
        [{tradingPair}] Chart Interval: {chartInterval}
      </Typography>
      
      <Box ref={chartContainerRef} sx={styles.chartContainer} />
      
        <Box ref={rsiChartContainerRef} sx={styles.rsiChartContainer} />
    
      
      {candles && candles.length > 0 && (
        <Box sx={styles.infoContainer}>
          <Typography variant="caption" color="textSecondary">
            Last update: {formatTime(candles[candles.length - 1].closeTime)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Price: ${parseFloat(candles[candles.length - 1].close).toFixed(2)}
          </Typography>
          {rsiData && rsiData.length > 0 && (
            <Typography variant="caption" color="textSecondary">
              RSI: {rsiData[rsiData.length - 1].rsi.toFixed(2)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const styles = {
  container: {
    fontFamily: "Roboto, sans-serif",
    padding: 2,
    margin: 1,
    fontSize: 10,
    textAlign: 'center',
    width: '100%',
    position: 'relative',
    height: "48vh",
    display: 'flex',
    flexDirection: 'column' as 'column',
  },
  title: {
    marginBottom: 2,
    fontSize: '0.9rem',
  },
  chartContainer: {
    width: '100%',
    flex: '7 0 0',
  },
  rsiChartContainer: {
    width: '100%',
    flex: '3 0 0',
    marginTop: 1,
  },
  controlsContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: 1,
    marginTop: 1,
  },
  infoContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: 1,
    marginTop: 1,
  }
};
